import styled from 'styled-components';

import { COLORS, AFFILIATE_REVEAL, MOBILE, REDUCED_MOTION, SPACE } from '@/nova/theme/tokens';
import { softCard } from '@/nova/theme/mixins';
import { Button, Reveal, Text } from '@/nova/components';
import { AffCard, HeaderRow } from '../../affiliates.styles';

/** A titled block: an uppercase eyebrow above its own Panel. Used for both the pending-invitations
 *  block (above the roster) and the affiliates block, so the two read as matching sections. */
export const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SectionEyebrow = styled(Text)`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${COLORS.textMuted};
  padding-left: 4px;
`;

export const PendingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/** A compact pending-invite row: avatar + email/expiry on the left, the copy-link + revoke actions on
 *  the right, in ONE row so the card is only about as tall as the avatar. Not clickable (no hoverWash /
 *  pointer): the row's own buttons are the only interactive parts, so the card must not read as openable. */
export const PendingCard = styled.div`
  ${softCard}
  padding: 10px 14px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

/** The email with its expiry stacked underneath, beside the avatar. */
export const PendingMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const PendingEmail = styled(Text)`
  font-size: 15px;
  font-weight: 600;
  color: ${COLORS.heading};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** The row's right-hand actions (Copy link + Revoke), pinned right and never shrinking. */
export const PendingActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

/** Short time-to-expiry under the email (e.g. "Expires in 5d"). */
export const ExpiryText = styled(Text)`
  font-size: 12px;
  color: ${COLORS.textMuted};
  white-space: nowrap;
`;

/** Wraps the header + the collapsible pending block as ONE column so the block can fully
 *  collapse (its height AND its own top spacing) without leaving a phantom gap: the page Stack
 *  applies its gap around this whole region, not around the pending block, so a hidden block reads
 *  identically to having no pending invites at all. No gap of its own: the header/block spacing
 *  lives inside PendingRevealInner so it collapses with the reveal. */
export const PendingRegion = styled.div`
  display: flex;
  flex-direction: column;
`;

/** The show/hide collapse for the pending-invitations block. Height animates via a 0fr→1fr grid
 *  row (not a max-height guess) so any number of invite rows + a "Load more" collapse cleanly; it
 *  stays MOUNTED when hidden (height 0) so the reveal runs both ways. Mirrors the players tray. */
export const PendingReveal = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${(p) => (p.$open ? '1fr' : '0fr')};
  transition: grid-template-rows ${AFFILIATE_REVEAL.SECTION_MS}ms ${AFFILIATE_REVEAL.EASE};

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/** The clipping row: overflow:hidden is what makes the 0fr row visually collapse. The top spacing
 *  that separates the block from the header lives HERE (padding-top) so it collapses with the
 *  height instead of holding the region open. The content also fades + rises slightly as it opens,
 *  and goes visibility:hidden once closed so it holds no tab stop behind the header. */
export const PendingRevealInner = styled.div<{ $open: boolean }>`
  min-height: 0;
  overflow: hidden;
  padding-top: 16px;
  opacity: ${(p) => (p.$open ? 1 : 0)};
  transform: translateY(${(p) => (p.$open ? '0' : '-4px')});
  visibility: ${(p) => (p.$open ? 'visible' : 'hidden')};
  transition-property: opacity, transform, visibility;
  transition-duration: ${AFFILIATE_REVEAL.CONTENT_MS}ms;
  transition-timing-function: ${AFFILIATE_REVEAL.EASE};

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/** Always-visible holder for a failed invites load, matching the pending block's top spacing so the
 *  error sits exactly where the block would. It is never placed behind the show/hide toggle. */
export const PendingErrorHolder = styled.div`
  padding-top: 16px;
`;

export const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/** The avatar + name, grouped on the left of the card's top row so the name can truncate while the
 *  status/chevron stay pinned right. */
export const NameGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

/** Initial-in-a-circle avatar, same deterministic-colour mnemonic as the players listing (its
 *  avatarColor/avatarInitial helpers), to make the roster more scannable. Decorative (aria-hidden);
 *  the name sits right beside it. */
export const Avatar = styled.span<{ $bg: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${(p) => p.$bg};
  user-select: none;
`;

export const AvatarInitial = styled(Text)<{ $fg: string }>`
  font-weight: 700;
  font-size: 15px;
  line-height: 1;
  color: ${(p) => p.$fg};
`;

/** Groups the header's Invite + Create buttons on the right of the HeaderRow. */
export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};

  ${MOBILE} {
    width: 100%;
  }
`;

/** The roster header: title + count, then the actions. On a phone it stacks, so the actions get their own row
 *  instead of squeezing the title and wrapping every label. */
export const RosterHeader = styled(HeaderRow)`
  ${MOBILE} {
    flex-direction: column;
    align-items: stretch;
  }
`;

/** "Invite affiliate": its natural width beside the title, the full row on a phone. */
export const InviteButton = styled(Button)`
  ${MOBILE} {
    && {
      display: flex;
      width: 100%;
    }
  }
`;

/** The show/hide invitations toggle in the header: desktop only (a phone gets {@link MobilePendingToggle}). */
export const HeaderPendingToggle = styled(Button)`
  ${MOBILE} {
    && {
      display: none;
    }
  }
`;

/** The show/hide invitations toggle on a phone: full width, after the invitations block, so it sits under the list
 *  while it is open and right under "Invite affiliate" once it is folded. */
export const MobilePendingToggle = styled(Button)`
  && {
    display: none;
  }

  ${MOBILE} {
    && {
      display: flex;
      width: 100%;
      margin-top: ${SPACE[12]};
    }
  }
`;

// A loading placeholder that borrows the real affiliate card's look (softCard,
// padding, stat grid) but is inert: no pointer cursor and no hover wash, since
// there is nothing to click yet.
export const SkeletonAffCard = styled(AffCard)`
  cursor: default;
  pointer-events: none;
`;

// Block-level crossfade for the roster region: the skeleton cards fade out as the
// real list fades in. Reveal's default (stacked) variant is inline-grid for inline
// values; widen it to a full-width grid so the overlapping card lists lay out and
// swap in place. The grid-cell overlap (both layers in 1/1) comes from Reveal itself.
export const RosterReveal = styled(Reveal)`
  /* && beats Reveal's own single-class \`display: inline-grid\` regardless of insertion order. */
  && {
    display: grid;
    width: 100%;
  }
`;
